import { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { levyCrosscheckAuditURL, levyCrosscheckFixURL, levyCrosscheckURL } from "../../../../utils/urls";

// ── Excel parsing ────────────────────────────────────────────────────────────
const HEADER_ALIASES = {
    name: ["name", "customer", "customer name"],
    unit: ["unit", "unit no", "unit number"],
    phone: ["phone#", "phone", "phone no", "phone number", "mobile"],
    email: ["email", "e-mail"],
    kraPin: ["kra pin", "kra", "pin", "kra_pin"],
    invoicingDate: ["invoicing date", "invoice date", "inception date", "start date"],
};

const findKey = (keys, aliases) =>
    keys.find((k) => aliases.includes(String(k).trim().toLowerCase()));

// Sheet dates are M/D/YYYY. Real Excel dates are shifted +12h so a few seconds of
// float error before midnight can't roll the day back.
const toIsoDate = (v) => {
    if (v === null || v === undefined || v === "") return null;
    let d = null;
    if (v instanceof Date) d = new Date(v.getTime() + 12 * 3600 * 1000);
    else if (typeof v === "number") {
        const p = XLSX.SSF.parse_date_code(v);
        if (p) d = new Date(p.y, p.m - 1, p.d, 12);
    } else {
        const m = String(v).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (m) d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]), 12);
        else if (!isNaN(new Date(v))) d = new Date(v);
    }
    if (!d || isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00.000Z`;
};

const parseSheet = async (file) => {
    const wb = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true });
    if (!json.length) throw new Error("The sheet is empty");
    const keys = Object.keys(json[0]);
    const map = {};
    Object.entries(HEADER_ALIASES).forEach(([field, aliases]) => { map[field] = findKey(keys, aliases); });
    if (!map.name || !map.unit) throw new Error("Could not find the 'Name' and 'Unit' columns");
    return json
        .map((r) => ({
            name: String(r[map.name] ?? "").trim(),
            unit: String(r[map.unit] ?? "").trim(),
            phone: map.phone ? String(r[map.phone] ?? "").trim() : "",
            email: map.email ? String(r[map.email] ?? "").trim() : "",
            kraPin: map.kraPin ? String(r[map.kraPin] ?? "").trim() : "",
            invoicingDate: map.invoicingDate ? toIsoDate(r[map.invoicingDate]) : null,
        }))
        .filter((r) => r.unit || r.name);
};

// ── Presentation helpers ─────────────────────────────────────────────────────
const STATUS_STYLE = {
    OK: { label: "Matches", color: "#2e7d32", bg: "#e8f5e9" },
    ISSUES: { label: "Has issues", color: "#e65100", bg: "#fff3e0" },
    NO_CONTRACT: { label: "No contract", color: "#c62828", bg: "#ffebee" },
    NO_UNIT: { label: "Unit not found", color: "#6a1b9a", bg: "#f3e5f5" },
};

const StatusBadge = ({ status }) => {
    const s = STATUS_STYLE[status] || STATUS_STYLE.OK;
    return (
        <span style={{ color: s.color, background: s.bg, padding: "3px 10px", borderRadius: 12, fontWeight: 600, fontSize: "0.82rem" }}>
            {s.label}
        </span>
    );
};

const fmtDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "-");

const FILTERS = [
    { label: "All rows", value: "ALL" },
    { label: "Has issues", value: "ISSUES" },
    { label: "No contract", value: "NO_CONTRACT" },
    { label: "Unit not found", value: "NO_UNIT" },
    { label: "Matches", value: "OK" },
];

const CrosscheckModal = ({ visible, onHide, onChanged }) => {
    const fileRef = useRef(null);
    const [fileName, setFileName] = useState("");
    const [sheetRows, setSheetRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("ALL");
    const [search, setSearch] = useState("");
    const [expandedRows, setExpandedRows] = useState(null);
    const [selected, setSelected] = useState({}); // key -> true
    const [edits, setEdits] = useState({}); // key -> edited value
    const [reason, setReason] = useState("");
    const [applying, setApplying] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);

    const reset = () => {
        setFileName(""); setSheetRows([]); setData(null); setSelected({}); setEdits({});
        setExpandedRows(null); setFilter("ALL"); setSearch(""); setReason("");
        if (fileRef.current) fileRef.current.value = "";
    };

    const runCheck = async (rows = sheetRows) => {
        const facilityId = await getItem("selectedFacilityId");
        if (!facilityId) { toastify("No facility selected.", "error"); return; }
        setLoading(true);
        try {
            const res = await makeRequest2(`${levyCrosscheckURL}/${facilityId}`, "POST", { rows }, { timeout: 120000 });
            const payload = res?.data;
            if (res?.success && payload?.success) {
                setData(payload.data);
                setSelected({}); setEdits({}); setExpandedRows(null);
            } else {
                toastify(payload?.error || res?.error || "Crosscheck failed", "error");
            }
        } catch (e) {
            toastify(e.message || "Crosscheck failed", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const rows = await parseSheet(file);
            setFileName(file.name);
            setSheetRows(rows);
            await runCheck(rows);
        } catch (err) {
            toastify(err.message || "Could not read the file", "error");
        }
    };

    const loadAudit = async () => {
        const facilityId = await getItem("selectedFacilityId");
        if (!facilityId) return;
        setAuditLoading(true);
        try {
            const res = await makeRequest2(`${levyCrosscheckAuditURL}/${facilityId}?limit=300`, "GET");
            setAuditLogs(res?.data?.data || []);
        } catch {
            setAuditLogs([]);
        } finally {
            setAuditLoading(false);
        }
    };

    // ── Fix selection ────────────────────────────────────────────────────────
    const issueKey = (row, i) => `${row.rowIndex}:${i}`;
    const allFixable = useMemo(() => {
        const out = [];
        (data?.results || []).forEach((r) => r.issues.forEach((iss, i) => {
            if (iss.fixable) out.push({ key: issueKey(r, i), issue: iss });
        }));
        return out;
    }, [data]);

    const toggle = (key) => setSelected((p) => ({ ...p, [key]: !p[key] }));
    const selectAllFixable = () => {
        const next = {}; allFixable.forEach((f) => { next[f.key] = true; }); setSelected(next);
    };
    const selectedCount = Object.values(selected).filter(Boolean).length;

    const applySelected = async () => {
        const facilityId = await getItem("selectedFacilityId");
        const fixes = allFixable
            .filter((f) => selected[f.key])
            .map((f) => ({ ...f.issue.fix, value: edits[f.key] !== undefined ? edits[f.key] : f.issue.fix.value }));
        if (!fixes.length) { toastify("Select at least one correction.", "warn"); return; }
        if (!window.confirm(`Apply ${fixes.length} correction(s)? Each change is saved to the audit log.`)) return;
        setApplying(true);
        try {
            const res = await makeRequest2(`${levyCrosscheckFixURL}/${facilityId}`, "POST", { fixes, reason }, { timeout: 120000 });
            const out = res?.data?.data;
            if (res?.success && out) {
                toastify(`${out.applied} applied${out.failed ? `, ${out.failed} failed` : ""}`, out.failed ? "warn" : "success");
                if (out.failed) console.warn("Crosscheck fix failures:", out.outcomes.filter((o) => !o.success));
                if (onChanged) onChanged();
                await runCheck(); // re-check so the screen shows what is still wrong
            } else {
                toastify(res?.data?.error || res?.error || "Could not apply corrections", "error");
            }
        } catch (e) {
            toastify(e.message || "Could not apply corrections", "error");
        } finally {
            setApplying(false);
        }
    };

    // ── Export of the comparison ─────────────────────────────────────────────
    const exportResults = () => {
        if (!data) return;
        const detail = data.results.map((r) => ({
            Row: r.rowIndex, Unit: r.excel.unit, "Sheet Name": r.excel.name, "System Name": r.customer?.name || "",
            Status: STATUS_STYLE[r.status]?.label || r.status,
            Issues: r.issues.map((i) => `${i.label}: sheet="${i.excel ?? ""}" system="${i.db ?? ""}"`).join(" | "),
        }));
        const missing = data.missingFromSheet.map((m) => ({ Unit: m.unit, Customer: m.customer, Contract: m.contractName }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detail), "Crosscheck");
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(missing), "In system not in sheet");
        XLSX.writeFile(wb, `levy_crosscheck_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (data?.results || []).filter((r) =>
            (filter === "ALL" || r.status === filter) &&
            (!q || r.excel.unit.toLowerCase().includes(q) || r.excel.name.toLowerCase().includes(q)));
    }, [data, filter, search]);

    // ── Row expansion: issues with editable corrected values ─────────────────
    const rowExpansion = (row) => (
        <div className="p-3" style={{ background: "#fafafa" }}>
            {row.contracts.length > 0 && (
                <div className="mb-2" style={{ fontSize: "0.85rem" }}>
                    <strong>Contract(s):</strong>{" "}
                    {row.contracts.map((c) => `${c.contractName} (${c.status}, ${c.paymentFrequency}, from ${fmtDate(c.startDate)})`).join("; ")}
                </div>
            )}
            {row.issues.length === 0 && <div style={{ color: "#2e7d32" }}>Everything matches for this unit.</div>}
            {row.issues.map((iss, i) => {
                const key = issueKey(row, i);
                const editable = iss.fixable;
                const val = edits[key] !== undefined ? edits[key] : (iss.fix?.value ?? "");
                const showVal = iss.fix?.field === "startDate" ? fmtDate(val) : val;
                return (
                    <div key={key} className="d-flex align-items-start gap-2 mb-2" style={{ borderBottom: "1px solid #eee", paddingBottom: 8 }}>
                        <input type="checkbox" disabled={!editable} checked={!!selected[key]} onChange={() => toggle(key)} style={{ marginTop: 6 }} />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>{iss.label}</div>
                            {iss.code === "MISSING_INVOICE" ? (
                                <div style={{ fontSize: "0.85rem", color: "#c62828" }}>
                                    Expected invoice: {iss.excel}
                                    <div style={{ fontSize: "0.78rem", color: "#888" }}>Not generated yet - generate it from the contract, then re-check.</div>
                                </div>
                            ) : iss.code === "DUPLICATE_INVOICE" ? (
                                <div style={{ fontSize: "0.85rem" }}>
                                    <div style={{ color: "#c62828" }}>{iss.db}</div>
                                    <div style={{ color: "#555" }}>{iss.excel} — only one active invoice is allowed per quarter.</div>
                                    <div style={{ fontSize: "0.78rem", color: editable ? "#e65100" : "#888" }}>
                                        {editable ? "Ticking this will VOID the duplicate (same as the Void Invoice action)." : iss.note}
                                    </div>
                                </div>
                            ) : iss.code === "DESCRIPTION_MISMATCH" ? (
                                <>
                                    <div style={{ fontSize: "0.82rem", color: "#c62828" }}>Current: {iss.db || "(empty)"}</div>
                                    {editable && (
                                        <InputText className="w-100 mt-1" value={val}
                                            onChange={(e) => { setEdits((p) => ({ ...p, [key]: e.target.value })); setSelected((p) => ({ ...p, [key]: true })); }} />
                                    )}
                                </>
                            ) : (
                                <div style={{ fontSize: "0.85rem" }}>
                                    <span style={{ color: "#555" }}>Sheet:</span> {String(iss.excel ?? "-")}{" "}
                                    <span style={{ color: "#555", marginLeft: 12 }}>System:</span> {String(iss.db ?? "-")}
                                    {editable && (
                                        <div className="mt-1" style={{ maxWidth: 360 }}>
                                            <span style={{ fontSize: "0.78rem", color: "#555" }}>Correct system value to:</span>
                                            <InputText className="w-100" value={showVal}
                                                disabled={iss.fix.field === "startDate" || iss.fix.field === "paymentFrequency"}
                                                onChange={(e) => { setEdits((p) => ({ ...p, [key]: e.target.value })); setSelected((p) => ({ ...p, [key]: true })); }} />
                                        </div>
                                    )}
                                </div>
                            )}
                            {!editable && iss.code !== "DUPLICATE_INVOICE" && iss.code !== "MISSING_INVOICE" && <div style={{ fontSize: "0.78rem", color: "#888" }}>Needs manual review — no automatic correction.</div>}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const summary = data?.summary;

    return (
        <Dialog
            visible={visible}
            onHide={() => { reset(); onHide(); }}
            header="Levy Crosscheck"
            style={{ width: "1100px", maxWidth: "96vw" }}
            modal
            onShow={() => { /* audit loads lazily when its tab opens */ }}
        >
            <TabView onTabChange={(e) => { if (e.index === 1) loadAudit(); }}>
                <TabPanel header="Crosscheck">
                    <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} className="form-control" style={{ maxWidth: 360 }} />
                        {fileName && <span style={{ fontSize: "0.85rem" }}>{fileName} · {sheetRows.length} rows</span>}
                        {data && <Button label="Re-check" icon="ti ti-refresh" className="p-button-sm p-button-outlined" onClick={() => runCheck()} loading={loading} />}
                        {data && <Button label="Export result" icon="ti ti-file-spreadsheet" className="p-button-sm p-button-outlined p-button-success" onClick={exportResults} />}
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#666" }} className="mb-3">
                        Expected columns: Name, Unit, Phone#, Email, KRA PIN, Invoicing Date (M/D/YYYY). Quarters are counted from the
                        month of each customer&apos;s invoicing date (e.g. May → May–Jul, Aug–Oct, Nov–Jan, Feb–Apr).
                    </div>

                    {loading && <div className="p-3 text-center">Comparing with the database…</div>}

                    {summary && !loading && (
                        <>
                            <div className="d-flex gap-2 flex-wrap mb-3">
                                {[
                                    ["Rows", summary.totalRows, "#37474f", "#eceff1"],
                                    ["Match", summary.ok, "#2e7d32", "#e8f5e9"],
                                    ["Issues", summary.withIssues, "#e65100", "#fff3e0"],
                                    ["No contract", summary.noContract, "#c62828", "#ffebee"],
                                    ["Unit not found", summary.unitNotFound, "#6a1b9a", "#f3e5f5"],
                                    ["In system, not in sheet", summary.missingFromSheet, "#00695c", "#e0f2f1"],
                                ].map(([l, n, c, bg]) => (
                                    <div key={l} style={{ background: bg, color: c, borderRadius: 8, padding: "6px 14px" }}>
                                        <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>{n}</div>
                                        <div style={{ fontSize: "0.75rem" }}>{l}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
                                <Dropdown value={filter} options={FILTERS} onChange={(e) => setFilter(e.value)} style={{ width: 180 }} />
                                <InputText placeholder="Search unit or name" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} />
                                <div style={{ flex: 1 }} />
                                <Button label={`Select all fixable (${allFixable.length})`} className="p-button-sm p-button-outlined" onClick={selectAllFixable} disabled={!allFixable.length} />
                                <Button label="Clear" className="p-button-sm p-button-text" onClick={() => setSelected({})} disabled={!selectedCount} />
                            </div>

                            <DataTable
                                value={rows} size="small" paginator rows={15} rowsPerPageOptions={[15, 50, 100]}
                                dataKey="rowIndex" expandedRows={expandedRows} onRowToggle={(e) => setExpandedRows(e.data)}
                                rowExpansionTemplate={rowExpansion} emptyMessage="No rows match this filter."
                            >
                                <Column expander style={{ width: "3rem" }} />
                                <Column field="rowIndex" header="#" style={{ width: "4rem" }} />
                                <Column field="excel.unit" header="Unit" />
                                <Column header="Sheet name" body={(r) => r.excel.name} />
                                <Column header="System customer" body={(r) => r.customer?.name || "-"} />
                                <Column header="Status" body={(r) => <StatusBadge status={r.status} />} />
                                <Column header="Issues" body={(r) => r.issues.length || "-"} style={{ width: "5rem" }} />
                            </DataTable>

                            {data.missingFromSheet.length > 0 && (
                                <details className="mt-3">
                                    <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                                        Active contracts in the system that are not in the sheet ({data.missingFromSheet.length})
                                    </summary>
                                    <DataTable value={data.missingFromSheet} size="small" paginator rows={10} className="mt-2">
                                        <Column field="unit" header="Unit" />
                                        <Column field="customer" header="Customer" />
                                        <Column field="contractName" header="Contract" />
                                    </DataTable>
                                </details>
                            )}

                            <div className="mt-3 p-3" style={{ background: "#f8f9fa", borderRadius: 6 }}>
                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                    <InputText placeholder="Reason for correction (saved to audit log)" value={reason}
                                        onChange={(e) => setReason(e.target.value)} style={{ flex: 1, minWidth: 260 }} />
                                    <Button label={`Apply ${selectedCount} correction${selectedCount === 1 ? "" : "s"}`}
                                        icon="ti ti-check" className="p-button-success" disabled={!selectedCount}
                                        loading={applying} onClick={applySelected} />
                                </div>
                                <div style={{ fontSize: "0.78rem", color: "#666" }} className="mt-1">
                                    Corrections update the customer, contract or invoice description, and can void duplicate invoices. Changing a contract&apos;s start date or
                                    frequency affects future invoices.
                                </div>
                            </div>
                        </>
                    )}
                </TabPanel>

                <TabPanel header="Audit log">
                    <div className="mb-2"><Button label="Refresh" icon="ti ti-refresh" className="p-button-sm p-button-outlined" onClick={loadAudit} loading={auditLoading} /></div>
                    <DataTable value={auditLogs} size="small" paginator rows={15} emptyMessage="No crosscheck changes recorded yet.">
                        <Column header="When" body={(l) => new Date(l.createdAt).toLocaleString()} />
                        <Column header="By" body={(l) => l.actor?.fullName || l.actor?.email || "-"} />
                        <Column field="description" header="Action" />
                        <Column header="Before" body={(l) => String(l.payload?.before ?? "-")} />
                        <Column header="After" body={(l) => String(l.payload?.after ?? "-")} />
                        <Column header="Reason" body={(l) => l.payload?.reason || "-"} />
                        <Column field="status" header="Status" />
                    </DataTable>
                </TabPanel>
            </TabView>
        </Dialog>
    );
};

export default CrosscheckModal;
